import {Box2, Matrix3x2} from "@cm/math"
import {assertNever} from "@cm/utils"
import type {DrawArgs, HalPainterBlendMode, HalPainterPrimitive, PainterPrimitivePaintArgs, ShaderReturnType} from "@cm/hal"
import {getMipLevelSize} from "@cm/hal"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {WebGl2Geometry} from "#webgl2/webgl2-geometry"
import type {WebGl2Image} from "#webgl2/webgl2-image"
import {checkForGlError} from "#webgl2/helpers/gl-error"
import {MAX_TEXTURE_UNITS} from "#webgl2/constants"
import {isWebGl2Image} from "#webgl2/helpers/is-web-gl2-image"
import type {WebGl2ShaderRef} from "#webgl2/webgl2-shader"
import {WebGl2Settings} from "#webgl2/settings"
import {loggerFactory} from "@cm/browser-log"

export class WebGl2PainterPrimitive implements HalPainterPrimitive {
    constructor(
        readonly context: WebGl2Context,
        private shadingFunction: string,
        private returnType?: ShaderReturnType,
    ) {
        WebGl2PainterPrimitive.instances?.add(this)

        if (MAX_TEXTURE_UNITS > this.context.maxTextureUnits) {
            throw Error(
                `WebGlLayerImagesComposite requires ${MAX_TEXTURE_UNITS} texture units, but the WebGlCanvas only supports ${this.context.maxTextureUnits}`,
            )
        }
    }

    // HalEntity
    dispose() {
        this.isDisposed = true
        this.shaderStandardRasterization?.release()
        this.shaderConservativeRasterization?.release()

        WebGl2PainterPrimitive.instances?.delete(this)
    }

    // HalPainterPrimitive
    paint({target, geometry, parameters, sourceImages, options}: PainterPrimitivePaintArgs) {
        if (this.isDisposed) {
            throw Error("WebGl2PainterPrimitive: Attempting to paint with a disposed painter")
        }
        if (!(geometry instanceof WebGl2Geometry)) {
            throw Error("Unsupported geometry")
        }
        const numIndices = geometry.numIndices
        if (numIndices >= 3) {
            const worldTransform = options?.transform ? Matrix3x2.fromMatrix3x2Like(options.transform) : new Matrix3x2()
            const uvTransform = options?.uvTransform ? Matrix3x2.fromMatrix3x2Like(options.uvTransform) : new Matrix3x2()
            const blendMode: HalPainterBlendMode = options?.blendMode ?? "none"
            const targetLayer = options?.targetLayer ?? 0
            const targetMipLevel = options?.targetMipLevel ?? 0
            const fullTargetRegion = Box2.fromSize(getMipLevelSize(target, targetMipLevel))
            const clipRegion = options?.clipRegion ? Box2.intersect(options.clipRegion, fullTargetRegion) : fullTargetRegion
            if (Box2.isEmpty(clipRegion)) {
                logger.warn("WebGl2PainterPrimitive: Clip region is empty, nothing to draw")
            } else {
                const drawArgs: DrawArgs = {
                    clipRegion: clipRegion,
                    transformOffset: {x: 0, y: 0},
                    uvTransformOffset: {x: 0, y: 0},
                    layer: targetLayer,
                    mipLevel: targetMipLevel,
                    isReplacing: false, //blendMode === "none", // TODO this is not correct in case the geometry does not cover the whole target
                }
                const imageAssignments: (WebGl2Image | undefined)[] = (sourceImages ? (Array.isArray(sourceImages) ? sourceImages : [sourceImages]) : []).map(
                    (sourceImage) => {
                        if (!sourceImage) {
                            return undefined
                        }
                        if (!isWebGl2Image(sourceImage)) {
                            throw Error("Unsupported source image")
                        }
                        sourceImage.prepareForUseAsSourceImage()
                        return sourceImage
                    },
                )
                if (imageAssignments.length > MAX_TEXTURE_UNITS) {
                    throw Error(`WebGlLayerImagesComposite requires ${MAX_TEXTURE_UNITS} source images, but ${imageAssignments.length} were provided`)
                }
                for (let i = imageAssignments.length; i < MAX_TEXTURE_UNITS; i++) {
                    imageAssignments.push(undefined)
                }
                const gl = this.context.gl
                const numPasses = target.beginDraw(drawArgs)
                const shaderInstance = this.getShader(geometry.conservativeRasterization)
                const numIndicesToDraw = geometry.prepareGeometry()
                shaderInstance.ref.setProgramAndData(geometry, imageAssignments)
                shaderInstance.ref.setUniforms(
                    target,
                    targetMipLevel,
                    worldTransform,
                    uvTransform,
                    geometry,
                    imageAssignments,
                    [1, 1, 1, 1],
                    parameters,
                    options?.conservativeRasterizationAddressMode,
                )
                this.setGlBlendMode(blendMode)
                for (let pass = 0; pass < numPasses; pass++) {
                    const viewTransformArgs = target.beginDrawPass(drawArgs, pass)
                    shaderInstance.ref.setViewTransform(viewTransformArgs)
                    logger.trace(
                        `WebGl2PainterPrimitive: Drawing ${numIndicesToDraw} indices, Draw args: `,
                        drawArgs,
                        ", transform: ",
                        viewTransformArgs.viewTransform.copy().append(worldTransform),
                    )
                    gl.drawElements(gl.TRIANGLES, numIndicesToDraw, gl.UNSIGNED_INT, 0)
                    checkForGlError(gl, "Calling drawElements")
                    target.endDrawPass(drawArgs, pass)
                }
                target.endDraw(drawArgs)
                shaderInstance.ref.unsetProgramAndData() // unbind textures to avoid potential subsequent feedback framebuffer operation
            }
        }
    }

    private getShader(conservativeRasterization: boolean) {
        if (conservativeRasterization) {
            if (!this.shaderConservativeRasterization) {
                this.shaderConservativeRasterization = this.context.createShader(this.shadingFunction, this.returnType, true)
            }
            return this.shaderConservativeRasterization
        } else {
            if (!this.shaderStandardRasterization) {
                this.shaderStandardRasterization = this.context.createShader(this.shadingFunction, this.returnType, false)
            }
            return this.shaderStandardRasterization
        }
    }

    private setGlBlendMode(blendMode: HalPainterBlendMode) {
        const gl = this.context.gl
        switch (blendMode) {
            case "none":
                gl.disable(gl.BLEND)
                break
            case "over":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.FUNC_ADD)
                gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
                break
            case "add":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.FUNC_ADD)
                gl.blendFunc(gl.ONE, gl.ONE)
                break
            case "sub":
                gl.enable(gl.BLEND)
                gl.blendEquationSeparate(gl.FUNC_REVERSE_SUBTRACT, gl.MAX) // TODO should this be separate ?
                gl.blendFunc(gl.ONE, gl.ONE)
                break
            case "mul":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.FUNC_ADD)
                gl.blendFunc(gl.DST_COLOR, gl.ZERO)
                break
            case "min":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.MIN)
                gl.blendFunc(gl.ONE, gl.ONE)
                break
            case "max":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.MAX)
                gl.blendFunc(gl.ONE, gl.ONE)
                break
            case "screen":
                gl.enable(gl.BLEND)
                gl.blendEquation(gl.FUNC_ADD)
                gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_COLOR)
                break
            default:
                assertNever(blendMode)
        }
    }

    private isDisposed = false
    private shaderStandardRasterization?: WebGl2ShaderRef
    private shaderConservativeRasterization?: WebGl2ShaderRef

    static instances = WebGl2Settings.InstanceTracking ? new Set<WebGl2PainterPrimitive>() : undefined
}

const logger = loggerFactory.getLogger("webgl2")
